import { PAGE_TITLE_SERVICE_NAME } from '@/constant/pageTitle';
import { STORIES_PATH } from '@/constant/routers/path';
import type { Component } from 'vue';
import { RouteRecordRaw } from 'vue-router';

// 動的インポート関数 - Vue Component型を使用
const About = (): Promise<Component> => import('@/pages/candidate/AboutPage.vue');
const Login = (): Promise<Component> => import('@/pages/candidate/LoginPage.vue');
const ForgotPassword = (): Promise<Component> => import('@/pages/candidate/ForgotPassword.vue');
const ResetPassword = (): Promise<Component> => import('@/pages/candidate/ResetPassword.vue');
const CompletedResetPassword = (): Promise<Component> =>
  import('@/pages/candidate/CompletedResetPassword.vue');
const CompletedBasicInfo = (): Promise<Component> =>
  import('@/pages/candidate/CompletedBasicInfo.vue');
const ProfilesEditPage = (): Promise<Component> =>
  import('@/pages/profiles-edit/ProfilesEditPage.vue');

const RegisterComplete = (): Promise<Component> =>
  import('@/pages/candidate/CompleteRegisterEmail.vue');
const ForgotPasswordComplete = (): Promise<Component> =>
  import('@/pages/candidate/CompleteForgotPassword.vue');
const VerifyPage = (): Promise<Component> => import('@/pages/candidate/VerifyPage.vue');
const VerifyResetPasswordPage = (): Promise<Component> =>
  import('@/pages/candidate/VerifyResetPasswordPage.vue');
const InterviewSchedulePage = (): Promise<Component> =>
  import('@/pages/interview_schedules/InterviewSchedulePage.vue');

const OfferReport = (): Promise<Component> => import('@/pages/offer_report/OfferReportPage.vue');
const CompletedOfferReport = (): Promise<Component> =>
  import('@/pages/offer_report/fin/CompletedOfferReportPage.vue');
const ViolationReportsPage = (): Promise<Component> =>
  import('@/pages/violation_reports/ViolationReportsPage.vue');
const FAQ = (): Promise<Component> => import('@/pages/candidate/FAQPage.vue');
const TermAndCondition = (): Promise<Component> =>
  import('@/pages/candidate/TermAndConditionPage.vue');
const PrivacyPolicy = (): Promise<Component> => import('@/pages/candidate/PrivacyPolicyPage.vue');
const Corporate = (): Promise<Component> => import('@/pages/corporate/CorporatePage.vue');
const NotFoundPage = (): Promise<Component> => import('@/pages/candidate/NotFoundPage.vue');
const AnnouncementDetail = (): Promise<Component> => import('@/pages/announcements/[id]/Index.vue');
const BasicInformationUpdatePassword = (): Promise<Component> =>
  import('@/pages/candidate/register/basic_information/UpdatePassword.vue');
const JobsPage = (): Promise<Component> => import('@/pages/jobs/JobsPage.vue');
const SignUpDirection = (): Promise<Component> =>
  import('@/pages/candidate/register/basic_information/SignUpDirection.vue');
const CpContact = (): Promise<Component> => import('@/pages/candidate/CpContact.vue');
const CpContactDone = (): Promise<Component> => import('@/pages/candidate/CpContactDone.vue');
const Contact = (): Promise<Component> => import('@/pages/candidate/Contact.vue');
const ContactDone = (): Promise<Component> => import('@/pages/candidate/ContactDone.vue');
const TeaserPage = (): Promise<Component> => import('@/pages/teaser/TeaserPage.vue');
const TeaserThanks = (): Promise<Component> => import('@/pages/teaser/thanks/TeaserThanksPage.vue');
const Partners = (): Promise<Component> => import('@/pages/partners.vue');
const Business = (): Promise<Component> => import('@/pages/business.vue');
const TopEntry = (): Promise<Component> => import('@/pages/top/TopEntryPage.vue');
const ConsiderationPage = (): Promise<Component> =>
  import('@/pages/consideration/ConsiderationPage.vue');
const FavoritePage = (): Promise<Component> => import('@/pages/favorite/FavoritePage.vue');
const MessagesPage = (): Promise<Component> => import('@/pages/messages/MessagesPage.vue');
const ScoutPage = (): Promise<Component> => import('@/pages/scouts/ScoutPage.vue');
const RecommendedJobsPage = (): Promise<Component> =>
  import('@/pages/recommended-jobs/RecommendedJobsPage.vue');
const ServicesLeavePage = (): Promise<Component> =>
  import('@/pages/services-leave/ServicesLeavePage.vue');
const ServicesLeaveConfirmPage = (): Promise<Component> =>
  import('@/pages/services-leave/ServicesLeaveConfirmPage.vue');
const CompanyPage = (): Promise<Component> => import('@/pages/company/CompanyPage.vue');
const JobDetailPage = (): Promise<Component> => import('@/pages/jobs/detail/JobDetailPage.vue');
const StoryDetailPage = (): Promise<Component> =>
  import('@/pages/company/stories/detail/StoryDetailPage.vue');
const CompanyStoriesPage = (): Promise<Component> =>
  import('@/pages/company/stories/CompanyStoriesPage.vue');
const LikedStoriesPage = (): Promise<Component> => import('@/pages/stories/LikedStoriesPage.vue');
const ServicesPage = (): Promise<Component> => import('@/pages/services/ServicesPage.vue');
const SettingInfoPage = (): Promise<Component> =>
  import('@/pages/setting-info/SettingInfoPage.vue');
const SettingNotificationPage = (): Promise<Component> =>
  import('@/pages/setting-notification/SettingNotificationPage.vue');
const ResumeGeneratorMainPage = (): Promise<Component> =>
  import('@/pages/resume-generator/ResumeGeneratorMainPage.vue');
const ResumeGeneratorConfirmPage = (): Promise<Component> =>
  import('@/pages/resume-generator/ResumeGeneratorConfirmPage.vue');
const ResumeGeneratorCompletePage = (): Promise<Component> =>
  import('@/pages/resume-generator/ResumeGeneratorCompletePage.vue');

const OFFER_REPORT_DOCUMENT_TITLE = `内定報告 | ${PAGE_TITLE_SERVICE_NAME}`;
const BREACH_REPORTS_DOCUMENT_TITLE = `違反報告 | ${PAGE_TITLE_SERVICE_NAME}`;

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'top',
    components: {
      default: TopEntry,
    },
  },
  {
    path: '/pages/top/auth',
    redirect: '/',
  },
  {
    path: '/pages/top/guest',
    redirect: '/',
  },
  {
    path: '/agent/auth/consideration_jobs',
    name: 'consideration_jobs',
    meta: { requiresAuth: true },
    components: {
      default: ConsiderationPage,
    },
  },
  {
    path: '/favorites/received',
    name: 'favorites_received',
    meta: { requiresAuth: true },
    components: {
      default: FavoritePage,
    },
  },
  {
    path: '/favorites/sent',
    name: 'favorites_sent',
    meta: { requiresAuth: true },
    components: {
      default: FavoritePage,
    },
  },
  {
    path: '/favorites/matched',
    name: 'favorites_matched',
    meta: { requiresAuth: true },
    components: {
      default: FavoritePage,
    },
  },
  {
    path: '/signup/new',
    meta: { requiresGuest: true },
    components: {
      default: SignUpDirection,
    },
  },
  {
    path: '/announcements/:id',
    name: 'announcement_detail',
    components: {
      default: AnnouncementDetail,
    },
    props: true,
  },
  {
    path: '/candidate/signup/update_password',
    components: {
      default: BasicInformationUpdatePassword,
    },
  },
  {
    path: '/candidate/about',
    name: 'about',
    components: {
      default: About,
    },
  },
  {
    path: '/candidate/login',
    name: 'login',
    meta: { requiresGuest: true },
    components: {
      default: Login,
    },
  },
  {
    path: '/candidate/forgot_password',
    name: 'forgot_password',
    meta: { requiresGuest: true },
    components: {
      default: ForgotPassword,
    },
  },
  {
    path: '/candidate/reset_password',
    name: 'reset_password',
    meta: { requiresGuest: true },
    components: {
      default: ResetPassword,
    },
  },
  {
    path: '/candidate/reset_password/completed',
    name: 'reset_password_completed',
    meta: { requiresGuest: true },
    components: {
      default: CompletedResetPassword,
    },
  },
  {
    path: '/candidate/completed_information',
    name: 'completed_information',
    components: {
      default: CompletedBasicInfo,
    },
  },
  {
    path: '/teaser',
    name: 'teaser',
    components: {
      default: TeaserPage,
    },
  },
  {
    path: '/teaser/thanks',
    name: 'teaser_thanks',
    components: {
      default: TeaserThanks,
    },
  },
  // {
  //   path: '/interview_schedules',
  //   name: 'interview_schedules',
  //   components: {
  //     default: InterviewSchedulePage,
  //   },
  // },
  {
    path: '/candidate/register_complete',
    name: 'register_complete',
    components: {
      default: RegisterComplete,
    },
  },
  {
    path: '/candidate/forgot_password_complete/:email',
    name: 'forgot_password_complete',
    meta: { requiresGuest: true },
    components: {
      default: ForgotPasswordComplete,
    },
    props: true,
  },
  {
    path: '/profiles/edit',
    name: 'edit_profile',
    meta: { requiresAuth: true },
    components: {
      default: ProfilesEditPage,
    },
  },
  {
    path: '/candidate/verify',
    name: 'verify',
    components: {
      default: VerifyPage,
    },
  },
  {
    path: '/candidate/verify_reset_password',
    name: 'verify_reset_password',
    meta: { requiresGuest: true },
    components: {
      default: VerifyResetPasswordPage,
    },
  },
  {
    path: '/offer_report',
    name: 'offer_report',
    meta: { documentTitle: OFFER_REPORT_DOCUMENT_TITLE },
    components: {
      default: OfferReport,
    },
  },
  {
    path: '/offer_report/fin',
    name: 'completed_offer_report',
    meta: { documentTitle: OFFER_REPORT_DOCUMENT_TITLE },
    components: {
      default: CompletedOfferReport,
    },
  },
  {
    path: '/breach_reports',
    name: 'breach_reports',
    meta: {
      requiresAuth: true,
      requiresCandidateResume: true,
      documentTitle: BREACH_REPORTS_DOCUMENT_TITLE,
    },
    components: {
      default: ViolationReportsPage,
    },
  },
  {
    path: '/faq',
    name: 'faq',
    meta: { usesHeadTitle: true },
    components: {
      default: FAQ,
    },
  },
  {
    path: '/terms',
    name: 'terms',
    meta: { usesHeadTitle: true },
    components: {
      default: TermAndCondition,
    },
  },
  {
    path: '/privacy',
    name: 'privacy',
    meta: { usesHeadTitle: true },
    components: {
      default: PrivacyPolicy,
    },
  },
  {
    path: '/corporate',
    name: 'corporate',
    components: {
      default: Corporate,
    },
  },
  {
    path: '/partners',
    name: 'Partners',
    component: Partners,
  },
  {
    path: '/business',
    name: 'business',
    component: Business,
  },
  {
    path: '/company/:companyId/stories',
    name: 'company_stories',
    meta: { usesHeadTitle: true },
    components: {
      default: CompanyStoriesPage,
    },
    props: true,
  },
  {
    // ストーリーは direct 求人を掲載する企業のみが持つため direct のみ提供する（agent ルートは追加しない）
    path: '/company/direct/:companyId(\\d+)/stories',
    name: 'company_stories_direct',
    meta: { usesHeadTitle: true },
    components: {
      default: CompanyStoriesPage,
    },
    props: true,
  },
  {
    path: '/jobs',
    name: 'jobs_direction',
    meta: { reloadAfterCrossTabAuthChange: true, usesHeadTitle: true },
    components: {
      default: JobsPage,
    },
  },
  {
    path: '/jobs/agent/:id(\\d+)',
    name: 'job_detail_agent',
    meta: { reloadAfterCrossTabAuthChange: true, usesHeadTitle: true },
    components: {
      default: JobDetailPage,
    },
    props: { default: true },
  },
  {
    path: '/jobs/direct/:id(\\d+)',
    name: 'job_detail_direct',
    meta: { reloadAfterCrossTabAuthChange: true, usesHeadTitle: true },
    components: {
      default: JobDetailPage,
    },
    props: { default: true },
  },
  {
    path: '/jobs/agent/:pathMatch(.*)*',
    name: 'jobs_direction_agent',
    meta: { reloadAfterCrossTabAuthChange: true, usesHeadTitle: true },
    components: {
      default: JobsPage,
    },
  },
  {
    path: '/jobs/direct/:pathMatch(.*)*',
    name: 'jobs_direction_direct',
    meta: { reloadAfterCrossTabAuthChange: true, usesHeadTitle: true },
    components: {
      default: JobsPage,
    },
  },
  {
    path: '/jobs/:id(\\d+)',
    name: 'job_detail',
    meta: { reloadAfterCrossTabAuthChange: true, usesHeadTitle: true },
    components: {
      default: JobDetailPage,
    },
    props: { default: true },
  },
  {
    path: '/company/management/jobs/:id(\\d+)',
    name: 'company_management_job_detail',
    meta: { suppressUnauthorizedRedirect: true },
    components: {
      default: JobDetailPage,
    },
    props: { default: true },
  },
  {
    path: '/company/:id/info',
    name: 'company_info',
    meta: { usesHeadTitle: true },
    components: {
      default: CompanyPage,
    },
    props: { default: true },
  },
  {
    path: '/company/agent/:id(\\d+)/info',
    name: 'company_info_agent',
    meta: { usesHeadTitle: true },
    components: {
      default: CompanyPage,
    },
    props: { default: true },
  },
  {
    path: '/company/direct/:id(\\d+)/info',
    name: 'company_info_direct',
    meta: { usesHeadTitle: true },
    components: {
      default: CompanyPage,
    },
    props: { default: true },
  },
  {
    path: '/company/:companyId/stories/:storyId',
    name: 'company_story_detail',
    meta: { usesHeadTitle: true },
    components: {
      default: StoryDetailPage,
    },
    props: { default: true },
  },
  {
    // ストーリーは direct 専用のため direct のみ提供する（agent ルートは追加しない）
    path: '/company/direct/:companyId(\\d+)/stories/:storyId',
    name: 'company_story_detail_direct',
    meta: { usesHeadTitle: true },
    components: {
      default: StoryDetailPage,
    },
    props: { default: true },
  },
  {
    path: '/cp-contact',
    name: 'cp_contact',
    meta: { suppressUnauthorizedRedirect: true, usesHeadTitle: true },
    components: {
      default: CpContact,
    },
  },
  {
    path: '/cp-contact/done',
    name: 'cp_contact_done',
    meta: { suppressUnauthorizedRedirect: true },
    components: {
      default: CpContactDone,
    },
  },
  {
    path: '/contact',
    name: 'contact',
    meta: { suppressUnauthorizedRedirect: true, usesHeadTitle: true },
    components: {
      default: Contact,
    },
  },
  {
    path: '/contact/done',
    name: 'contact_done',
    meta: { suppressUnauthorizedRedirect: true },
    components: {
      default: ContactDone,
    },
  },
  {
    path: '/messages',
    name: 'messages',
    meta: { requiresAuth: true },
    components: { default: MessagesPage },
  },
  {
    path: '/messages/:id',
    name: 'messages_show',
    meta: { requiresAuth: true },
    components: { default: MessagesPage },
    props: true,
  },
  {
    path: '/scouts',
    name: 'scouts',
    meta: { requiresAuth: true },
    components: { default: ScoutPage },
  },
  {
    path: '/scouts/:id',
    name: 'scouts_show',
    meta: { requiresAuth: true },
    components: { default: ScoutPage },
    props: true,
  },
  {
    path: '/recommended-jobs',
    name: 'recommended_jobs',
    meta: { requiresAuth: true },
    components: { default: RecommendedJobsPage },
  },
  {
    path: '/recommended-jobs/:id',
    name: 'recommended_jobs_show',
    meta: { requiresAuth: true },
    components: { default: RecommendedJobsPage },
    props: true,
  },
  {
    path: STORIES_PATH,
    name: 'stories',
    meta: {
      requiresAuth: true,
      documentTitle: `いいねしたストーリー一覧 | ${PAGE_TITLE_SERVICE_NAME}`,
    },
    components: { default: LikedStoriesPage },
  },
  {
    path: '/services',
    name: 'services',
    components: { default: ServicesPage },
  },
  {
    path: '/setting/info',
    name: 'setting_info',
    components: { default: SettingInfoPage },
  },
  {
    path: '/services/leave',
    name: 'services_leave',
    components: { default: ServicesLeavePage },
  },
  {
    path: '/services/leave/confirm',
    name: 'services_leave_confirm',
    components: { default: ServicesLeaveConfirmPage },
  },
  {
    path: '/setting/notification',
    name: 'setting_notification',
    meta: { requiresAuth: true },
    components: { default: SettingNotificationPage },
  },
  {
    path: '/resume-generator',
    name: 'resume_generator',
    meta: { requiresAuth: true },
    components: { default: ResumeGeneratorMainPage },
  },
  {
    path: '/resume-generator/confirm',
    name: 'resume_generator_confirm',
    meta: { requiresAuth: true },
    components: { default: ResumeGeneratorConfirmPage },
  },
  {
    path: '/resume-generator/complete',
    name: 'resume_generator_complete',
    meta: { requiresAuth: true },
    components: { default: ResumeGeneratorCompletePage },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not_found',
    components: {
      default: NotFoundPage,
    },
  },
];

export default routes;
